import Konva from "konva";
import { Node, NodeConfig } from "konva/types/Node";
import consts from "shared/consts";
import { Connector } from "shared/datamodel/schemas/connector";
import { IRect, Point } from "./math-utils";
import BoundingBox from "../geometry/bounding-box";
import { measureNode } from "./node-utils";
import { map } from "./fn-utils";

export type AlignmentTypes = "left" | "right" | "hcenter" | "top" | "bottom" | "vcenter";

export const AlignmentFns: Record<AlignmentTypes, (n: Konva.Node[]) => IMoveData[]> = {
  left: alignLeft,
  right: alignRight,
  hcenter: alignHcenters,
  top: alignTop,
  bottom: alignBottom,
  vcenter: alignVcenters,
};

export interface IMoveData {
  node: Konva.Node;
  source: Point;
  target: Point;
}

function sum<T>(list: Iterable<T>, keyFn: (t: T) => number) {
  let result = 0;
  for (const item of list) result += keyFn(item);
  return result;
}

function canAlignObject(node: Konva.Node) {
  if (node.attrs.element.type == consts.CANVAS_ELEMENTS.CONNECTOR) {
    const connector = node.attrs.element as Connector;
    const free = connector.anchorsMode?.every((x) => x == "n/a");
    return free;
  }
  return true;
}

interface IMeasure {
  readonly all: BoundingBox;
  recordNode(node: Konva.Node, visualRect: IRect, position: Point, groupId: string): void;
}

// A note about measurements.
// The element position we save in replicache doesn't correspond to the visual bounding box of the node,
// because of stroke and shadows (task card have shadows).
// When aligning nodes we want them to align visually, not just have the same x/y.
// So we calculate how much they have to move to align visually, and that is the delta
// we give the data model. That's why we keep both in the data object below
// note: don't be tempted to calculate visual rect without stroke. won't align well.
class NodeMeasurements {
  constructor(
    readonly node: Konva.Node,
    readonly visualRect: IRect,
    readonly position: Point,
    readonly groupid?: string
  ) {}
}

class LegacyMeasurements implements IMeasure {
  public readonly nodes: NodeMeasurements[] = [];
  public readonly groups: Record<string, BoundingBox> = {};
  public readonly all = new BoundingBox();

  recordNode(node: Node<NodeConfig>, visualRect: IRect, position: Point, groupId: string): void {
    if (groupId) {
      this.groups[groupId] ??= new BoundingBox();
      this.groups[groupId].expandRect(visualRect);
    }
    this.nodes.push(new NodeMeasurements(node, visualRect, position, groupId));
    this.all.expandRect(visualRect);
  }
}

class NewMeasurements implements IMeasure {
  private ungrouped: [IRect, NodeMeasurements[]][] = [];
  private grouped: Record<string, [any, NodeMeasurements[]]> = {};
  public readonly all = new BoundingBox();

  recordNode(node: Node<NodeConfig>, visualRect: IRect, position: Point, groupId: string): void {
    const dataItem = new NodeMeasurements(node, visualRect, position, groupId);
    if (groupId) {
      this.grouped[groupId] ??= [new BoundingBox(), []];
      this.grouped[groupId][0].union(visualRect);
      this.grouped[groupId][1].push(dataItem);
    } else {
      this.ungrouped.push([visualRect, [dataItem]]);
    }
    this.all.expandRect(visualRect);
  }

  getItems() {
    return [...this.ungrouped, ...Object.values(this.grouped)];
  }
}

function measureAllNodes(nodes: Konva.Node[], measurer: IMeasure): IMeasure {
  const elementsLayer = nodes[0].getLayer(); // I take for granted that all Konva's layers are similarily transformed
  if (!elementsLayer) throw new Error("Asked to measure node that isn't in a layer");
  const options = { skipShadow: true };
  for (let i = 0; i < nodes.length; i++) {
    const node = nodes[i];
    if (!canAlignObject(node)) continue;
    const rect = measureNode(node, options);
    measurer.recordNode(node, rect, { x: node.attrs.x, y: node.attrs.y }, node.attrs.element.groupId);
  }
  return measurer;
}

function alignGeneric(
  measurements: LegacyMeasurements,
  calcGroupDeltaMove: (groupBox: BoundingBox) => Point,
  calcNodeNewPosition: (move: NodeMeasurements) => Point
) {
  // first calculate movement info for groups
  const groupMovement = map(calcGroupDeltaMove, measurements.groups)!;
  // now calculate for elements
  const moves: IMoveData[] = [];
  for (const node of measurements.nodes) {
    // if node is in group, we move it as part of the group
    if (node.groupid) {
      const delta = groupMovement[node.groupid],
        x = node.position.x + delta.x,
        y = node.position.y + delta.y;
      moves.push({ node: node.node, source: node.position, target: { x, y } });
    } else {
      moves.push({ node: node.node, source: node.position, target: calcNodeNewPosition(node) });
    }
  }
  return moves;
}

function alignLeft(nodes: Konva.Node[]) {
  const measurements = new LegacyMeasurements();
  measureAllNodes(nodes, measurements);
  const left = measurements.all.x;
  return alignGeneric(
    measurements,
    (groupBBox) => ({ x: left - groupBBox.x, y: 0 }),
    (node) => ({ y: node.position.y, x: left - (node.visualRect.x - node.position.x) })
  );
}

function alignRight(nodes: Konva.Node[]) {
  const measurements = new LegacyMeasurements();
  measureAllNodes(nodes, measurements);
  const right = measurements.all.right;
  return alignGeneric(
    measurements,
    (groupBBox) => ({ x: right - groupBBox.right, y: 0 }),
    (node) => ({ y: node.position.y, x: right - (node.visualRect.x + node.visualRect.width - node.position.x) })
  );
}

function alignBottom(nodes: Konva.Node[]) {
  const measurements = new LegacyMeasurements();
  measureAllNodes(nodes, measurements);
  const bottom = measurements.all.bottom;
  return alignGeneric(
    measurements,
    (groupBBox) => ({ x: 0, y: bottom - groupBBox.bottom }),
    (node) => ({ y: bottom - (node.visualRect.y + node.visualRect.height - node.position.y), x: node.position.x })
  );
}

function alignTop(nodes: Konva.Node[]) {
  const measurements = new LegacyMeasurements();
  measureAllNodes(nodes, measurements);
  const top = measurements.all.top;
  return alignGeneric(
    measurements,
    (groupBBox) => ({ x: 0, y: top - groupBBox.top }),
    (node) => ({ x: node.position.x, y: top - (node.visualRect.y - node.position.y) })
  );
}

function alignHcenters(nodes: Konva.Node[]) {
  const measurements = new LegacyMeasurements();
  measureAllNodes(nodes, measurements);
  const mid = (measurements.all.left + measurements.all.right) / 2;

  return alignGeneric(
    measurements,
    (groupBBox) => ({ x: mid - (groupBBox.x + groupBBox.width / 2), y: 0 }),
    (node) => ({ x: mid - (node.visualRect.x + node.visualRect.width / 2 - node.position.x), y: node.position.y })
  );
}

function alignVcenters(nodes: Konva.Node[]) {
  const measurements = new LegacyMeasurements();
  measureAllNodes(nodes, measurements);
  const mid = (measurements.all.top + measurements.all.bottom) / 2;
  return alignGeneric(
    measurements,
    (groupBBox) => ({ x: 0, y: mid - (groupBBox.top + groupBBox.height / 2) }),
    (node) => ({ x: node.position.x, y: mid - (node.visualRect.y + node.visualRect.height / 2 - node.position.y) })
  );
}

export function distributeH(nodes: Konva.Node[]) {
  const measurements = new NewMeasurements();
  measureAllNodes(nodes, measurements);
  const temp = measurements.getItems();
  temp.sort((a, b) => a[0].x - b[0].x);
  const leftmost = temp[0];
  const gap = (measurements.all.width - sum(temp, ([rect]) => rect.width)) / (temp.length - 1);
  let mark = leftmost[0].x + leftmost[0].width;

  const moves: IMoveData[] = [];
  for (let i = 1; i < temp.length - 1; i++) {
    const rect = temp[i][0];
    const group = temp[i][1];
    const groupDelta = mark + gap - rect.x;
    for (const item of group) {
      moves.push({
        node: item.node,
        source: item.position,
        target: {
          x: item.position.x + groupDelta,
          y: item.position.y,
        },
      });
    }
    mark += gap + rect.width;
  }
  return moves;
}

// distribute vertically.
// the same as horizontal, only with y,height instead of x,width
export function distributeV(nodes: Konva.Node[]) {
  const measurements = new NewMeasurements();
  measureAllNodes(nodes, measurements);
  const temp = measurements.getItems();
  temp.sort((a, b) => a[0].y - b[0].y);
  const topmost = temp[0];
  const gap = (measurements.all.height - sum(temp, ([rect]) => rect.height)) / (temp.length - 1);
  let mark = topmost[0].y + topmost[0].height;

  const moves: IMoveData[] = [];
  for (let i = 1; i < temp.length - 1; i++) {
    const rect = temp[i][0];
    const group = temp[i][1];
    const groupDelta = mark + gap - rect.y;
    for (const item of group) {
      moves.push({
        node: item.node,
        source: item.position,
        target: {
          x: item.position.x,
          y: item.position.y + groupDelta,
        },
      });
    }
    mark += gap + rect.height;
  }
  return moves;
}
